import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SoundCard from '../components/SoundCard';
import AudioPlayer from '../components/AudioPlayer';
import {soundsData} from '../data/soundsData';
import * as FiIcons from 'react-icons/fi';
const {FiVolume2}=FiIcons;

function SoundsGrid() {
  const [selectedCategory,setSelectedCategory]=useState('all');
  const [currentSound,setCurrentSound]=useState(null);

  const categories=[
    {id: 'all',name: 'All Sounds',color: 'bg-[#445a68]'},
    {id: 'consonants',name: 'Consonants',color: 'bg-[#b5c9c8]'},
    {id: 'vowels',name: 'Vowels',color: 'bg-[#ccacb2]'},
    {id: 'stops',name: 'Stops',color: 'bg-[#57838d]'},
    {id: 'liquids',name: 'Liquids',color: 'bg-[#f2bdb1]'},
    {id: 'nasals',name: 'Nasals',color: 'bg-[#445a68]'},
    {id: 'affricates',name: 'Affricates',color: 'bg-[#b5c9c8]'},
    {id: 'glides',name: 'Glides',color: 'bg-[#ccacb2]'},
    {id: 'fricatives',name: 'Fricatives',color: 'bg-[#57838d]'}
  ];

  const getFilteredSounds=()=> {
    if (selectedCategory==='all') return soundsData;
    if (selectedCategory==='vowels' || selectedCategory==='consonants') {
      return soundsData.filter(sound=> sound.category===selectedCategory);
    }
    return soundsData.filter(sound=> sound.subcategory===selectedCategory);
  };

  const getConsonantsBySubcategory=()=> {
    const subcategories=['stops','liquids','nasals','affricates','glides','fricatives'];
    return subcategories.map(sub=> ({
      name: sub.charAt(0).toUpperCase() + sub.slice(1),
      sounds: soundsData.filter(sound=> sound.subcategory===sub)
    })).filter(group=> group.sounds.length > 0);
  };

  const getVowelsByGroup=()=> {
    // Short Vowels: apple,bed,itch,octopus,up
    const shortVowelIds=['a-apple','e-bed','i-itch','o-octopus','u-up'];
    const shortVowels=shortVowelIds.map(id=> soundsData.find(sound=> sound.id===id) ).filter(Boolean);

    // Long Vowels: cake,bee,bike,boat,glue,cube
    const longVowelIds=['a-cake','e-bee','i-bike','o-boat','u-glue','yu-cube'];
    const longVowels=longVowelIds.map(id=> soundsData.find(sound=> sound.id===id) ).filter(Boolean);

    // Other Vowels: car,corn,her,claw,book,boy,cow,banana
    const otherVowelIds=['ar-car','or-corn','er-her','aw-claw','oo-book','oy-boy','ow-cow','schwa-banana'];
    const otherVowels=otherVowelIds.map(id=> soundsData.find(sound=> sound.id===id) ).filter(Boolean);

    return [
      {name: 'Short Vowels',sounds: shortVowels},
      {name: 'Long Vowels',sounds: longVowels},
      {name: 'Other Vowels',sounds: otherVowels}
    ];
  };

  const handleSoundPlay=(sound)=> {
    setCurrentSound({...sound,timestamp: Date.now()});
  };

  const getCurrentCategoryName=()=> {
    const category=categories.find(cat=> cat.id===selectedCategory);
    return category ? category.name : '';
  };

  const getTextColor=(bgColor)=> {
    // Determine if text should be black or white based on background
    const lightColors=['bg-[#b5c9c8]','bg-[#ccacb2]','bg-[#f2bdb1]'];
    return lightColors.includes(bgColor) ? 'text-black' : 'text-white';
  };

  const renderSoundsGrid=(sounds)=> (
    <div className="flex justify-center w-full">
      <div className="flex flex-wrap justify-center gap-4 max-w-6xl">
        {sounds.map((sound,index)=> (
          <motion.div
            key={sound.id}
            initial={{scale: 0.8,opacity: 0}}
            animate={{scale: 1,opacity: 1}}
            transition={{duration: 0.4,delay: index * 0.05}}
          >
            <SoundCard
              sound={sound}
              onPlay={()=> handleSoundPlay(sound)}
              isPlaying={false}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderAllSoundsGrouped=()=> (
    <div className="space-y-12">
      {/* Consonants Section */}
      <motion.div
        initial={{y: 20,opacity: 0}}
        animate={{y: 0,opacity: 1}}
        transition={{duration: 0.6}}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-[#445a68] mb-8">
          Consonants
        </h2>
        <div className="space-y-8">
          {getConsonantsBySubcategory().map((group,groupIndex)=> (
            <motion.div
              key={group.name}
              initial={{y: 20,opacity: 0}}
              animate={{y: 0,opacity: 1}}
              transition={{duration: 0.6,delay: groupIndex * 0.1}}
              className="text-center w-full"
            >
              <h3 className="text-xl font-semibold text-[#445a68] mb-4">
                {group.name}
              </h3>
              {renderSoundsGrid(group.sounds)}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Vowels Section */}
      <motion.div
        initial={{y: 20,opacity: 0}}
        animate={{y: 0,opacity: 1}}
        transition={{duration: 0.6,delay: 0.3}}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-[#445a68] mb-8">
          Vowels
        </h2>
        <div className="space-y-8">
          {getVowelsByGroup().map((group,groupIndex)=> (
            <motion.div
              key={group.name}
              initial={{y: 20,opacity: 0}}
              animate={{y: 0,opacity: 1}}
              transition={{duration: 0.6,delay: groupIndex * 0.1}}
              className="text-center w-full"
            >
              <h3 className="text-xl font-semibold text-[#445a68] mb-4">
                {group.name}
              </h3>
              {renderSoundsGrid(group.sounds)}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg,#e8f0ef 0%,#f0e8ed 50%,#fae8e0 100%)'}}>
      {/* Header - Sticky on desktop, normal on mobile */}
      <motion.header
        className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40 md:sticky md:top-0"
        initial={{y: -50,opacity: 0}}
        animate={{y: 0,opacity: 1}}
        transition={{duration: 0.6}}
        style={{position: 'relative'}}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="text-center mb-4 sm:mb-6">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-2">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-[#57838d] rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiVolume2} className="text-white text-lg sm:text-2xl" />
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold text-[#445a68]">Virtual Sound Wall</h1>
            </div>
            <p className="text-sm sm:text-lg text-black opacity-70">
              Click any sound to hear the phoneme
            </p>
          </div>

          {/* Category Buttons */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {categories.map(category=> (
              <motion.button
                key={category.id}
                onClick={()=> setSelectedCategory(category.id)}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 ${
                  selectedCategory===category.id
                    ? `${category.color} ${getTextColor(category.color)} shadow-lg scale-105`
                    : `${category.color} opacity-70 ${getTextColor(category.color)} hover:opacity-100 hover:scale-105`
                }`}
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
              >
                {category.name}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <motion.div
          initial={{y: 20,opacity: 0}}
          animate={{y: 0,opacity: 1}}
          transition={{duration: 0.6,delay: 0.2}}
        >
          {/* Category Title - Only show when not "All Sounds" */}
          {selectedCategory !=='all' && (
            <motion.div
              className="text-center mb-6 sm:mb-8"
              initial={{y: -20,opacity: 0}}
              animate={{y: 0,opacity: 1}}
              transition={{duration: 0.6}}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-[#445a68]">
                {getCurrentCategoryName()}
              </h2>
            </motion.div>
          )}

          {selectedCategory==='all' ? (
            renderAllSoundsGrouped()
          ) : selectedCategory==='consonants' ? (
            <div className="space-y-6 sm:space-y-8">
              {getConsonantsBySubcategory().map((group,groupIndex)=> (
                <motion.div
                  key={group.name}
                  initial={{y: 20,opacity: 0}}
                  animate={{y: 0,opacity: 1}}
                  transition={{duration: 0.6,delay: groupIndex * 0.1}}
                  className="text-center w-full"
                >
                  <h3 className="text-lg sm:text-xl font-semibold text-[#445a68] mb-3 sm:mb-4">
                    {group.name}
                  </h3>
                  {renderSoundsGrid(group.sounds)}
                </motion.div>
              ))}
            </div>
          ) : selectedCategory==='vowels' ? (
            <div className="space-y-6 sm:space-y-8">
              {getVowelsByGroup().map((group,groupIndex)=> (
                <motion.div
                  key={group.name}
                  initial={{y: 20,opacity: 0}}
                  animate={{y: 0,opacity: 1}}
                  transition={{duration: 0.6,delay: groupIndex * 0.1}}
                  className="text-center w-full"
                >
                  <h3 className="text-lg sm:text-xl font-semibold text-[#445a68] mb-3 sm:mb-4">
                    {group.name}
                  </h3>
                  {renderSoundsGrid(group.sounds)}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center w-full">
              {renderSoundsGrid(getFilteredSounds())}
            </div>
          )}
        </motion.div>
      </main>

      {/* Audio Player */}
      <AudioPlayer currentSound={currentSound} />
    </div>
  );
}

export default SoundsGrid;